import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Alert } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { ITherapy } from '../../../util/types';
import LoadingSpinner from '../../../components/LoadingSpinner';
import AnswerAndActivity from '../components/AnswerAndActivity';
import TherapyDetails from '../components/TherapyDetails';
import TherapySection from '../components/TherapySection';
import PatientAppointments from '../components/PatientAppointments';
import ResendActivationBanner from '../components/ResendActivationBanner';
import PromQuestionDetails from '../components/PromQuestionDetails';
import TrainingPlanDetails from '../components/TrainingPlanDetails';

const TherapyDetailsPage: FC = (): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  const { user } = useAuth0();
  const { therapyId } = useParams<{ therapyId: string }>();

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [therapy, setTherapy] = useState<ITherapy>();

  useEffect(() => {
    if (!user || !therapyId) {
      return;
    }

    const run = async (): Promise<void> => {
      try {
        const therapyData: ITherapy = await authFetch(`/api/v1/therapies/${therapyId}`);

        setTherapy(therapyData);
      } catch (error) {
        console.error('Error occured while fetching patient details data', error);
      } finally {
        setIsFetchingData(false);
      }
    };

    void run();
  }, [user, therapyId]);

  if (isFetchingData) {
    return <LoadingSpinner />;
  }

  if (!therapy) {
    return (
      <Alert variant="danger" className="m-3">
        {i18next.t('generic.error')}
      </Alert>
    );
  }

  return (
    <>
      <ResendActivationBanner therapy={therapy} />

      <TherapyDetails therapy={therapy}>
        <div className="d-grid gap-3">
          <TherapySection
            eventKey="appointments"
            icon="fas fa-calendar-alt"
            title={i18next.t('therapyDetails.appointments.title')}
            description={i18next.t('therapyDetails.appointments.description')}
          >
            <PatientAppointments therapy={therapy} />
          </TherapySection>

          <TherapySection
            eventKey="workouts"
            icon="fas fa-dumbbell"
            title={i18next.t('therapyDetails.workouts.title')}
            description={i18next.t('therapyDetails.workouts.description')}
          >
            <TrainingPlanDetails therapyId={therapy.id} />
          </TherapySection>

          <TherapySection
            eventKey="questionnaires"
            icon="fas fa-clipboard-list"
            title={i18next.t('therapyDetails.questionnaire.title')}
            description={i18next.t('therapyDetails.questionnaire.description')}
          >
            <PromQuestionDetails therapy={therapy} />
          </TherapySection>

          <TherapySection
            eventKey="answer-and-activity"
            icon="fas fa-chart-line"
            title={i18next.t('therapyDetails.answerAndActivity.title')}
            description={i18next.t('therapyDetails.answerAndActivity.description')}
          >
            <AnswerAndActivity therapyId={therapy.id} />
          </TherapySection>
        </div>
      </TherapyDetails>
    </>
  );
};

export default TherapyDetailsPage;
